import { Search, X } from 'lucide-react';
import { useId } from 'react';

import { cn } from '@/lib/utils';

/**
 * A search box for filtering a list.
 *
 * `type="search"`, a real label, and a clear button that appears once there is
 * something to clear — a permanently visible X on an empty field is a control
 * that does nothing.
 *
 * Debouncing is the caller's job, not this component's. Whether a keystroke
 * should hit the server depends on what is being searched, and a debounce baked
 * in here would also delay filtering a list already in memory.
 */
export function SearchInput({
  value,
  onChange,
  label = 'Search',
  placeholder,
  className,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Visually hidden but read out. Say what is being searched. */
  label?: string;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}) {
  const id = useId();

  return (
    <div className={cn('relative', className)}>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>

      <Search
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-ink-500"
      />

      <input
        id={id}
        name="search"
        type="search"
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="min-h-control w-full rounded-sm border border-ink-300 bg-white pl-10 pr-10 text-body placeholder:text-ink-500"
      />

      {value ? (
        <button
          type="button"
          aria-label={`Clear ${label.toLowerCase()}`}
          onClick={() => {
            onChange('');
          }}
          // Absolutely positioned and full height, so the target is the whole
          // right end of the field rather than a 16px glyph.
          className="absolute inset-y-0 right-0 grid min-w-touch place-items-center text-ink-500 hover:text-ink-900"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      ) : null}
    </div>
  );
}
